<template>
  <a-spin :spinning="isInviting">
    <a-input
      :placeholder="$t('shared.searchInputPlaceHolder')"
      @input="e => search(e.target.value)"
    >
      <template #prefix>
        <UserOutlined type="user" />
      </template>
    </a-input>
    <div class="alignment-contact-list">
      <div
        v-if="isLoading"
        :style="{ textAlign: 'center' }"
      >
        <a-spin
          size="small"
        />
      </div>
      <template v-else>
        <div
          v-if="isFrequentContactsVisible"
          class="frequent-contacts-title"
        >
          {{ $t('shared.frequentContacts') }}
        </div>
        <a-space direction="vertical">
          <a-checkbox
            v-for="user in users"
            :key="user.uid"
            :checked="entity.mentionedUserIds.includes(user.uid)"
            :disabled="user.uid === myUserId"
            @change="onCheckUser(user.uid, $event.target.checked)"
          >
            <AlignUser :user="user" />
          </a-checkbox>
        </a-space>
      </template>
    </div>
  </a-spin>
</template>

<script>
import { ref, computed, toRaw } from 'vue'
import { useStore } from 'vuex'
import { UserOutlined } from '@ant-design/icons-vue'
import { editObjectiveAuth, removeObjectiveAuth, editKeyResultAuth, removeKeyResultAuth } from '@/api/auth'

import {
  updateObjective,
  updateKeyResult
} from '@/api/okr'
import {
  getMyObjective,
  getMyKeyResult
} from '@/graphql/okr'
import useSearchUser from '@/composables/use-search-user'
import AlignUser from './AlignUser'
import useMention from '@/composables/use-mention'

export default {
  name: 'AlignInvitation',
  components: {
    UserOutlined,
    AlignUser
  },
  inheritAttrs: false,
  props: {
    entity: {
      type: Object,
      required: true
    }
  },
  emits: [
    'invited'
  ],
  setup (props, { emit }) {
    const { state } = useStore()
    const myUserId = computed(() => state.user.uid)
    const isInviting = ref(false)
    const { users, search, isLoading, isFrequentContactsVisible } = useSearchUser('ALL')
    const { getAddUserIds, getRmUserIds } = useMention()
    let addUserIds, rmUserIds
    function updateContent (content, mentionedUserIds) {
      const fn = props.entity.type === 'objective' ? updateObjective : updateKeyResult
      const originalMentionedUserIds = toRaw(props.entity.mentionedUserIds)
      addUserIds = getAddUserIds(originalMentionedUserIds, mentionedUserIds)
      rmUserIds = getRmUserIds(originalMentionedUserIds, mentionedUserIds)
      return fn(props.entity.id, {
        content,
        mention_user_ids: [[6, 0, mentionedUserIds]]
      })
    }

    function mentionUser (user) {
      const newContent = props.entity.content + ` @${user.name}`
      return updateContent(newContent, [
        ...props.entity.mentionedUserIds,
        user.uid
      ])
    }

    function removeMentionedUser (user) {
      const newContent = props.entity.content
        .replaceAll(`@${user.nickname}`, '')
        .replaceAll(`@${user.name}`, '').trim()
      return updateContent(newContent, props.entity.mentionedUserIds.filter(id => id !== user.uid))
    }

    function getUpdatedEntity (id) {
      const fn = props.entity.type === 'objective' ? getMyObjective : getMyKeyResult
      return fn(id)
    }

    function onCheckUser (id, checked) {
      isInviting.value = true
      const user = users.value.find(user => user.uid === id)
      const fn = checked ? mentionUser : removeMentionedUser
      fn(user)
        .then(() => getUpdatedEntity(props.entity.id))
        .then(updatedEntity => {
          addAuth(addUserIds)
          removeAuth(rmUserIds)
          emit('invited', updatedEntity)
          isInviting.value = false
        })
    }

    function addAuth (addUserIds) {
      if (addUserIds.length === 0) {
        return
      }
      const fn = props.entity.type === 'objective' ? editObjectiveAuth : editKeyResultAuth
      fn(props.entity.id, addUserIds).then(res => {
        console.log('add auth success')
      })
    }

    function removeAuth (rmUserIds) {
      if (rmUserIds.length === 0) {
        return
      }
      const fn = props.entity.type === 'objective' ? removeObjectiveAuth : removeKeyResultAuth
      fn(props.entity.id, rmUserIds).then(res => {
        console.log('remove auth success')
      })
    }

    return {
      users,
      search,
      isLoading,
      isInviting,
      isFrequentContactsVisible,
      onCheckUser,
      myUserId
    }
  }
}
</script>

<style lang="less" scoped>
:deep(.ant-checkbox-wrapper) {
  display: flex;
  align-items: center;
}
</style>
